import { useMemo, useRef, useState } from "react";
import Badge from "@/components/base/Badge";
import ExportMenu from "@/components/base/ExportMenu";
import PaiementAbonnementModal from "@/components/feature/PaiementAbonnementModal";
import { cn } from "@/utils/cn";
import StatCard from "@/pages/proprietaire/components/StatCard";
import { useBiens } from "@/pages/proprietaire/biens/BiensProvider";
import { useContrats } from "@/pages/proprietaire/contrats/ContratsProvider";
import { useImmeubles } from "@/pages/proprietaire/immeubles/ImmeublesProvider";
import { useLocataires } from "@/pages/proprietaire/locataires/LocatairesProvider";
import type { ExportColumn } from "@/utils/export";
import { formatDate, formatFcfa } from "@/utils/format";
import { useFormules } from "@/store/formules/FormulesProvider";
import { remiseAnnuellePourcent } from "@/config/formules";
import { useAbonnement } from "./AbonnementProvider";
import FormuleCard from "./components/FormuleCard";
import UsageBar from "./components/UsageBar";
import {
  ABONNEMENT_STATUT_LABELS,
  ABONNEMENT_STATUT_TONES,
  getFormule,
  toEspaceFormules,
  type Formule,
  type FormuleId,
  type Periodicite,
} from "./types";

const periodOptions: { value: Periodicite; label: string }[] = [
  { value: "mensuel", label: "Mensuel" },
  { value: "annuel", label: "Annuel" },
];

function InfoTile({ label, value, icon }: { label: string; value: string; icon: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="flex items-center gap-1.5 text-xs text-foreground-500">
        <i className={cn(icon, "text-sm")} aria-hidden="true" />
        {label}
      </span>
      <span className="text-sm font-label font-medium text-foreground-950">{value}</span>
    </div>
  );
}

function formatLimite(valeur: number | null, base: string): string {
  if (valeur === null) return `${base}s illimités`;
  return `${valeur} ${base}${valeur > 1 ? "s" : ""}`;
}

export default function AbonnementPage() {
  const { abonnement, setPeriodicite, changerFormule, rafraichir, expire } = useAbonnement();
  const { immeubles } = useImmeubles();
  const { stats: biensStats } = useBiens();
  const { locataires } = useLocataires();
  const { contrats } = useContrats();
  const formulesRef = useRef<HTMLDivElement>(null);
  const [paiementFormule, setPaiementFormule] = useState<FormuleId | null>(null);
  const [essaiAvertissement, setEssaiAvertissement] = useState("");

  // Tarifs lus dans Supabase (source de vérité unique) : les modifications du
  // Super Admin sont reflétées ici sans changement de code.
  const { formules: catalogueFormules } = useFormules();
  const FORMULES = useMemo(() => toEspaceFormules(catalogueFormules), [catalogueFormules]);
  const remiseAnnuelle = useMemo(() => {
    const payante = catalogueFormules.find((item) => item.prixMensuel > 0);
    return payante ? remiseAnnuellePourcent(payante) : 0;
  }, [catalogueFormules]);

  const formule = getFormule(abonnement.formuleId, FORMULES);
  const formuleAffichee = getFormule(abonnement.formuleAfficheeId, FORMULES);
  const paiementRequis = abonnement.paiementRequis;
  const demandePaiement = abonnement.demandePaiement;
  const demandeEnAttente = paiementRequis && demandePaiement?.statut === "en_attente";
  const demandeRefusee = paiementRequis && demandePaiement?.statut === "rejete";
  const gratuit = formuleAffichee.prixMensuel === 0;
  const estEssai = abonnement.estEssai;
  const periodeLabel = abonnement.periodicite === "annuel" ? "Annuel" : "Mensuel";

  const usage = useMemo(
    () => [
      { key: "immeubles", label: "Immeubles", icon: "ri-building-2-line", used: immeubles.length, limite: estEssai ? null : formule.limites.immeubles },
      { key: "biens", label: "Biens", icon: "ri-home-4-line", used: biensStats.total, limite: estEssai ? null : formule.limites.biens },
      { key: "locataires", label: "Locataires", icon: "ri-user-3-line", used: locataires.length, limite: estEssai ? null : formule.limites.locataires },
      { key: "contrats", label: "Contrats", icon: "ri-file-text-line", used: contrats.length, limite: estEssai ? null : formule.limites.contrats },
      { key: "utilisateurs", label: "Utilisateurs", icon: "ri-team-line", used: 1, limite: estEssai ? null : formule.limites.utilisateurs },
    ],
    [immeubles.length, biensStats.total, locataires.length, contrats.length, formule, estEssai]
  );

  const limites = estEssai
    ? [
        "Immeubles illimités",
        "Biens illimités",
        "Locataires illimités",
        "Contrats illimités",
        "Utilisateurs illimités",
      ]
    : [
        formatLimite(formule.limites.immeubles, "immeuble"),
        formatLimite(formule.limites.biens, "bien"),
        formatLimite(formule.limites.locataires, "locataire"),
        formatLimite(formule.limites.contrats, "contrat"),
        formatLimite(formule.limites.utilisateurs, "utilisateur"),
      ];

  const scrollToFormules = () => {
    formulesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleChoose = (id: FormuleId) => {
    const choix = getFormule(id, FORMULES);
    // Pendant l'essai gratuit ACTIF, AUCUNE formule (payante OU gratuite) ne peut
    // être souscrite : le serveur refuse tout remplacement tant que l'essai court,
    // et l'essai ne doit jamais être écrasé par la formule Gratuit avant son terme.
    if (estEssai) {
      setEssaiAvertissement(
        `Votre essai gratuit est actif jusqu'au ${abonnement.essaiDateFin ? formatDate(abonnement.essaiDateFin) : "terme"}. Vous pourrez choisir une formule à son terme.`
      );
      return;
    }
    if (choix.prixMensuel === 0) {
      // Formule gratuite : activation immédiate, sans paiement.
      changerFormule(id, abonnement.periodicite);
      scrollToFormules();
      return;
    }
    // Formule payante : demande de paiement local (Orange / Moov / Espèces).
    setPaiementFormule(id);
  };

  const paiementFormuleData = paiementFormule ? getFormule(paiementFormule, FORMULES) : null;
  const paiementMontant = paiementFormuleData
    ? abonnement.periodicite === "annuel"
      ? paiementFormuleData.prixAnnuel
      : paiementFormuleData.prixMensuel
    : 0;

  const exportColumns: ExportColumn<Formule>[] = [
    { header: "Formule", value: (item) => item.nom },
    { header: "Prix mensuel (FCFA)", value: (item) => item.prixMensuel },
    { header: "Prix annuel (FCFA)", value: (item) => item.prixAnnuel },
    { header: "Immeubles", value: (item) => item.limites.immeubles ?? "Illimité" },
    { header: "Biens", value: (item) => item.limites.biens ?? "Illimité" },
    { header: "Locataires", value: (item) => item.limites.locataires ?? "Illimité" },
    { header: "Contrats", value: (item) => item.limites.contrats ?? "Illimité" },
    { header: "Utilisateurs", value: (item) => item.limites.utilisateurs ?? "Illimité" },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="max-w-2xl">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">Abonnement</h2>
          <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
            Consultez votre formule, ses limites et l'utilisation réelle de votre espace. Vous pouvez changer de
            formule ou renouveler votre abonnement à tout moment.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <ExportMenu
            rows={FORMULES}
            columns={exportColumns}
            filename="formules-abonnement"
            title="Formules d'abonnement"
          />
          <button
            type="button"
            onClick={scrollToFormules}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-4 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            <i className="ri-exchange-line text-base" aria-hidden="true" />
            Changer de formule
          </button>
          <button
            type="button"
            onClick={() => (gratuit || paiementRequis || estEssai ? undefined : setPaiementFormule(abonnement.formuleId))}
            disabled={gratuit || paiementRequis || estEssai}
            className={cn(
              "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-label font-medium transition-colors",
              gratuit || paiementRequis || estEssai
                ? "cursor-not-allowed border border-background-300 bg-background-100 text-foreground-400"
                : "cursor-pointer bg-primary-500 text-background-50 hover:bg-primary-600"
            )}
          >
            <i className="ri-refresh-line text-base" aria-hidden="true" />
            Renouveler
          </button>
        </div>
      </div>

      {estEssai && (
        <div className="flex flex-col gap-1.5 rounded-lg border border-secondary-300 bg-secondary-50 px-4 py-3 text-sm text-secondary-900">
          <p className="flex items-start gap-2.5">
            <i className="ri-gift-2-line mt-0.5 text-base" aria-hidden="true" />
            <span>
              <strong>Offre de lancement — essai gratuit 6 mois, accès complet.</strong> Votre accès complet est offert
              {abonnement.essaiDateFin ? (
                <>
                  {" "}jusqu'au <strong>{formatDate(abonnement.essaiDateFin)}</strong>
                </>
              ) : null}
              {typeof abonnement.joursRestantsEssai === "number" ? (
                <> ({abonnement.joursRestantsEssai} jour{abonnement.joursRestantsEssai > 1 ? "s" : ""} restant{abonnement.joursRestantsEssai > 1 ? "s" : ""}).</>
              ) : null}{" "}
              À la fin de l'essai, choisissez une formule payante pour conserver l'accès complet.
            </span>
          </p>
        </div>
      )}

      {expire && (
        <div className="flex items-start gap-2.5 rounded-lg border border-accent-300 bg-accent-50 px-4 py-3 text-sm text-accent-900">
          <i className="ri-error-warning-line mt-0.5 text-base" aria-hidden="true" />
          <p>
            Votre abonnement est expiré. Vous pouvez consulter et exporter vos données, mais la création et la
            modification sont bloquées jusqu'au renouvellement.
          </p>
        </div>
      )}

      {paiementRequis && (
        <div
          className={cn(
            "flex flex-col gap-3 rounded-lg border px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between",
            demandeRefusee
              ? "border-primary-200 bg-primary-50/60 text-primary-900"
              : "border-accent-200 bg-accent-50/70 text-accent-900"
          )}
        >
          <p className="flex items-start gap-2.5">
            <i
              className={cn("mt-0.5 text-base", demandeRefusee ? "ri-close-circle-line" : "ri-time-line")}
              aria-hidden="true"
            />
            <span>
              Vous avez choisi la formule <strong>{formuleAffichee.nom}</strong>
              {demandeEnAttente ? (
                <>
                  {" "}— votre paiement est <strong>en attente de validation</strong>. En attendant, votre espace
                  fonctionne avec la formule <strong>Gratuite</strong>. Aucune nouvelle saisie n'est nécessaire.
                </>
              ) : demandeRefusee ? (
                <>
                  {" "}— votre paiement a été <strong>refusé</strong>
                  {demandePaiement?.commentaire ? ` (motif : ${demandePaiement.commentaire})` : ""}. Vous pouvez
                  soumettre une nouvelle demande.
                </>
              ) : (
                <>
                  {" "}— elle sera activée après validation de votre paiement. En attendant, votre espace fonctionne
                  avec la formule <strong>Gratuite</strong>.
                </>
              )}
            </span>
          </p>
          {!demandeEnAttente && (
            <button
              type="button"
              onClick={() => setPaiementFormule(abonnement.formuleAfficheeId)}
              className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-secure-payment-line text-base" aria-hidden="true" />
              {demandeRefusee ? "Refaire la demande" : "Payer maintenant"}
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon="ri-vip-diamond-line"
          label={estEssai ? "Offre en cours" : paiementRequis ? "Formule choisie" : "Formule actuelle"}
          value={estEssai ? "Essai 6 mois" : formuleAffichee.nom}
          tone="primary"
        />
        <StatCard
          icon="ri-hand-coin-line"
          label="Prix mensuel"
          value={gratuit ? "0 FCFA" : formatFcfa(formuleAffichee.prixMensuel)}
          tone="secondary"
        />
        <StatCard
          icon="ri-calendar-2-line"
          label="Prix annuel"
          value={gratuit ? "0 FCFA" : formatFcfa(formuleAffichee.prixAnnuel)}
          tone="accent"
        />
        <StatCard
          icon="ri-shield-check-line"
          label="Statut"
          value={ABONNEMENT_STATUT_LABELS[abonnement.statut]}
          tone="neutral"
        />
      </div>

      <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
              <i className="ri-vip-diamond-line text-xl" aria-hidden="true" />
            </span>
            <div>
              <h3 className="font-heading text-base font-semibold text-foreground-950">
                {estEssai ? "Essai gratuit — accès complet" : `Formule ${formuleAffichee.nom}`}
              </h3>
              <p className="mt-0.5 text-sm text-foreground-600">
                {estEssai ? "Offre de lancement : 6 mois offerts avec l'accès à tous les modules." : formuleAffichee.tagline}
              </p>
            </div>
          </div>
          <Badge tone={ABONNEMENT_STATUT_TONES[abonnement.statut]}>{ABONNEMENT_STATUT_LABELS[abonnement.statut]}</Badge>
        </div>

        <dl className="mt-5 grid grid-cols-1 gap-4 border-t border-background-200 pt-5 sm:grid-cols-2 xl:grid-cols-4">
          <InfoTile icon="ri-timer-line" label="Période d'abonnement" value={periodeLabel} />
          <InfoTile
            icon="ri-play-circle-line"
            label="Date de début"
            value={abonnement.dateDebut ? formatDate(abonnement.dateDebut) : "Non définie"}
          />
          <InfoTile
            icon="ri-stop-circle-line"
            label="Date de fin"
            value={abonnement.dateFin ? formatDate(abonnement.dateFin) : "Non définie"}
          />
          <InfoTile
            icon="ri-price-tag-3-line"
            label="Prix de la période"
            value={
              abonnement.periodicite === "annuel"
                ? gratuit
                  ? "0 FCFA"
                  : `${formatFcfa(formuleAffichee.prixAnnuel)} / an`
                : gratuit
                  ? "0 FCFA"
                  : `${formatFcfa(formuleAffichee.prixMensuel)} / mois`
            }
          />
        </dl>

        {gratuit && !paiementRequis && !estEssai && (
          <p className="mt-4 flex items-start gap-2 rounded-md border border-background-200 bg-background-100 px-3.5 py-2.5 text-xs text-foreground-600">
            <i className="ri-information-line mt-0.5 text-sm text-foreground-500" aria-hidden="true" />
            La formule Gratuite ne comporte pas de période ni de renouvellement. Choisissez une formule payante pour
            activer la facturation mensuelle ou annuelle.
          </p>
        )}
      </section>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <section className="flex flex-col rounded-lg border border-background-200 bg-background-50">
          <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-50 text-secondary-600">
              <i className="ri-list-check-2 text-base" aria-hidden="true" />
            </span>
            <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">
              {estEssai
                ? "Accès complet (essai gratuit)"
                : paiementRequis
                  ? "Droits en cours (formule Gratuite)"
                  : "Limites de la formule"}
            </h3>
          </header>
          <ul className="flex flex-col gap-2.5 p-4 md:p-5">
            {limites.map((item) => (
              <li key={item} className="flex items-center gap-2 text-sm text-foreground-700">
                <i className="ri-check-line text-secondary-600" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
        </section>

        <section className="flex flex-col rounded-lg border border-background-200 bg-background-50">
          <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-50 text-accent-700">
              <i className="ri-pie-chart-line text-base" aria-hidden="true" />
            </span>
            <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">
              Utilisation actuelle
            </h3>
          </header>
          <div className="flex flex-col gap-5 p-4 md:p-5">
            {usage.map((item) => (
              <UsageBar key={item.key} label={item.label} icon={item.icon} used={item.used} limite={item.limite} />
            ))}
          </div>
        </section>
      </div>

      <div ref={formulesRef} className="flex flex-col gap-4 scroll-mt-24">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="font-heading text-base font-semibold text-foreground-950 md:text-lg">
              Changer de formule
            </h3>
            <p className="mt-0.5 text-sm text-foreground-600">
              Choisissez la formule adaptée à votre patrimoine et votre périodicité.
            </p>
          </div>
          <div className="inline-flex items-center gap-1 self-start rounded-full border border-background-200 bg-background-100 p-1">
            {periodOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setPeriodicite(option.value)}
                className={cn(
                  "rounded-full px-4 py-1.5 text-sm font-label font-medium transition-all duration-200 cursor-pointer",
                  abonnement.periodicite === option.value
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-700 hover:text-foreground-950"
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {essaiAvertissement && (
          <p className="flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-3.5 py-2.5 text-sm text-accent-900">
            <i className="ri-information-line mt-0.5 text-base" aria-hidden="true" />
            {essaiAvertissement}
          </p>
        )}

        {abonnement.periodicite === "annuel" && remiseAnnuelle > 0 && (
          <p className="text-sm font-label font-medium text-secondary-700">
            Économie de {remiseAnnuelle} % avec la facturation annuelle.
          </p>
        )}

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
          {FORMULES.map((item) => (
            <FormuleCard
              key={item.id}
              formule={item}
              current={!estEssai && item.id === abonnement.formuleId}
              paiementRequis={paiementRequis && item.prixMensuel > 0 && item.id === abonnement.formuleAfficheeId}
              paiementEnAttente={demandeEnAttente && item.id === abonnement.formuleAfficheeId}
              dateLimitePaiement={
                paiementRequis && item.prixMensuel > 0 && item.id === abonnement.formuleAfficheeId
                  ? abonnement.dateLimitePaiement
                  : ""
              }
              onChoose={handleChoose}
            />
          ))}
        </div>
      </div>

      <PaiementAbonnementModal
        open={paiementFormule !== null}
        onClose={() => setPaiementFormule(null)}
        formuleCode={paiementFormule ?? abonnement.formuleId}
        formuleNom={paiementFormuleData?.nom ?? formuleAffichee.nom}
        periodicite={abonnement.periodicite}
        montant={paiementMontant}
        onSuccess={() => void rafraichir()}
      />
    </div>
  );
}